import React, { useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import { useProjectStore } from '../../hooks/useProjectStore';
import { formationAPI } from '../../services/api';

// ═══════════════════════════════════════════════════════════
// 裂缝参数表 + 成像测井解释成果导入
//
// 导入格式: CSV / 制表符文本, 表头按关键字匹配 (中英文皆可):
//   深度|md, 倾角|dip, 倾向|azimuth, 张开度|aperture, 类型|type
// ═══════════════════════════════════════════════════════════

const TH = {
  text: '#1E2A3A', textSec: '#4F6680', textMuted: '#8598AD',
  border: '#D6DAE0', panelBg: '#F4F6F9', accent: '#C2533A', green: '#1E8449',
};

type Frac = {
  id: number; md: number; tvd: number | null; dip_angle: number; dip_azimuth: number;
  strike: number; aperture_mm: number | null; frac_type: string; source: string; confidence: number;
};

const TYPE_ZH: Record<string, string> = {
  open: '张开缝', filled: '充填缝', healed: '闭合缝', induced: '诱导缝', bedding: '层理缝',
};
const SRC_ZH: Record<string, string> = {
  image_log: '成像测井', core: '岩心', seismic: '地震属性', outcrop: '露头', manual: '人工',
};

/** 表头关键字 → 字段。中英混排的解释成果表都能认。 */
function pickColumn(headers: string[], keys: string[]): number {
  const low = headers.map((h) => h.toLowerCase().trim());
  for (const k of keys) {
    const i = low.findIndex((h) => h.includes(k));
    if (i >= 0) return i;
  }
  return -1;
}

function parseTable(text: string) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) throw new Error('内容少于两行，无法解析表头与数据。');
  const sep = lines[0].includes('\t') ? '\t' : ',';
  const headers = lines[0].split(sep);
  const iMd = pickColumn(headers, ['深度', 'md', 'depth']);
  const iDip = pickColumn(headers, ['倾角', 'dip_angle', 'dip角', 'dip']);
  const iAzi = pickColumn(headers, ['倾向', 'azimuth', 'azi']);
  const iAp = pickColumn(headers, ['张开', 'aperture', '开度']);
  const iType = pickColumn(headers, ['类型', 'type']);
  if (iMd < 0) throw new Error('未找到深度列 (表头需包含「深度」或 md/depth)。');
  if (iDip < 0) throw new Error('未找到倾角列 (表头需包含「倾角」或 dip)。');
  if (iAzi < 0) throw new Error('未找到倾向列 (表头需包含「倾向」或 azimuth)。');

  const out: any[] = [];
  const bad: number[] = [];
  for (let r = 1; r < lines.length; r++) {
    const c = lines[r].split(sep);
    const md = Number(c[iMd]), dip = Number(c[iDip]), azi = Number(c[iAzi]);
    if (!Number.isFinite(md) || !Number.isFinite(dip) || !Number.isFinite(azi)) { bad.push(r + 1); continue; }
    const rec: any = { md, dip_angle: dip, dip_azimuth: ((azi % 360) + 360) % 360 };
    if (iAp >= 0 && Number.isFinite(Number(c[iAp]))) rec.aperture_mm = Number(c[iAp]);
    if (iType >= 0 && c[iType]) {
      const t = c[iType].trim();
      const hit = Object.entries(TYPE_ZH).find(([k, v]) => t === v || t.toLowerCase() === k);
      if (hit) rec.frac_type = hit[0];
    }
    out.push(rec);
  }
  if (!out.length) throw new Error('没有解析出有效数据行。');
  return { rows: out, bad };
}

export default function FracTablePage() {
  const { currentProject } = useProjectStore();
  const projectId = currentProject?.id;
  const [rows, setRows] = useState<Frac[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const load = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const { data } = await formationAPI.listFractures(projectId);
      setRows(Array.isArray(data) ? data : data?.results ?? []);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (projectId) load(); }, [projectId]);

  const onFile = async (f: File) => {
    if (!projectId) return;
    setBusy(true);
    try {
      const text = await f.text();
      const { rows: parsed, bad } = parseTable(text);
      const { data } = await formationAPI.importFractures({
        project_id: projectId, fractures: parsed, source: 'image_log',
      });
      message.success(
        `导入成功 ${data.created} 条${bad.length ? `（跳过 ${bad.length} 行无效数据）` : ''}`);
      await load();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || e?.message || '导入失败');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  if (!projectId) return <div style={{ padding: 40, color: TH.textSec }}>请先选择项目</div>;

  return (
    <div style={{ padding: 20, fontFamily: 'inherit' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: TH.text, margin: 0 }}>裂缝参数</h2>
          <div style={{ fontSize: 12, color: TH.textMuted, marginTop: 3 }}>
            共 {rows.length} 条 · 数据来源以成像测井(FMI/EMI)解释成果为主
          </div>
        </div>
        <div>
          <input ref={fileRef} type="file" accept=".csv,.txt,.tsv" style={{ display: 'none' }}
            onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
          <button onClick={() => fileRef.current?.click()} disabled={busy}
            style={{ fontSize: 12, padding: '6px 16px', borderRadius: 4, fontWeight: 600, cursor: busy ? 'wait' : 'pointer', border: 'none', background: TH.accent, color: '#fff' }}>
            {busy ? '导入中…' : '⇧ 导入解释成果'}
          </button>
        </div>
      </div>

      <div style={{ background: TH.panelBg, border: `1px solid ${TH.border}`, borderRadius: 6, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: TH.textSec, lineHeight: 1.9 }}>
        <b style={{ color: TH.text }}>导入格式</b>：CSV 或制表符文本，首行为表头，按关键字匹配列（中英文均可）——
        <code>深度/md</code>、<code>倾角/dip</code>、<code>倾向/azimuth</code>、
        可选 <code>张开度/aperture</code>、<code>类型/type</code>。
        <span style={{ color: TH.textMuted }}>注意：导入为<b>替换式</b>，会先清空该项目已有的「成像测井」来源裂缝。</span>
      </div>

      {!rows.length ? (
        <div style={{ padding: 40, textAlign: 'center', color: TH.textSec, background: '#fff', borderRadius: 6, border: `1px solid ${TH.border}` }}>
          {loading ? '加载中…' : '暂无天然裂缝数据，点右上角导入解释成果。'}
        </div>
      ) : (
        <div style={{ background: '#fff', border: `1px solid ${TH.border}`, borderRadius: 6, overflow: 'hidden' }}>
          <div style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: TH.panelBg, position: 'sticky', top: 0 }}>
                  {['深度 MD (m)', '倾角 (°)', '倾向 (°)', '走向 (°)', '张开度 (mm)', '类型', '来源'].map((h) => (
                    <th key={h} style={{ padding: '7px 10px', textAlign: 'left', borderBottom: `1px solid ${TH.border}`, color: TH.textSec, fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td style={{ padding: '5px 10px', borderBottom: '1px solid #F0F2F5', fontFamily: 'monospace' }}>{r.md.toFixed(1)}</td>
                    <td style={{ padding: '5px 10px', borderBottom: '1px solid #F0F2F5', fontFamily: 'monospace' }}>{r.dip_angle.toFixed(1)}</td>
                    <td style={{ padding: '5px 10px', borderBottom: '1px solid #F0F2F5', fontFamily: 'monospace' }}>{r.dip_azimuth.toFixed(1)}</td>
                    <td style={{ padding: '5px 10px', borderBottom: '1px solid #F0F2F5', fontFamily: 'monospace', color: TH.textSec }}>{r.strike?.toFixed(1)}</td>
                    <td style={{ padding: '5px 10px', borderBottom: '1px solid #F0F2F5', fontFamily: 'monospace' }}>{r.aperture_mm != null ? r.aperture_mm.toFixed(2) : '—'}</td>
                    <td style={{ padding: '5px 10px', borderBottom: '1px solid #F0F2F5' }}>
                      <span style={{ color: r.frac_type === 'open' ? TH.green : TH.textSec }}>
                        {TYPE_ZH[r.frac_type] ?? r.frac_type}
                      </span>
                    </td>
                    <td style={{ padding: '5px 10px', borderBottom: '1px solid #F0F2F5', color: TH.textMuted }}>{SRC_ZH[r.source] ?? r.source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
